// ** Modules
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';

// ** UI
import Checkbox from '@mui/material/Checkbox';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import IconButton from '@mui/material/IconButton';

// ** Styles
import { DIN } from '@/styles/globalStyles';

const MotionTableRow = motion.create(TableRow);

// ** Types
import type { Employee, EmployeeTableColumn } from './EmployeeList.types';

const STRIPE_BG = '#F4F3F2';
const HOVER_BG = '#E8E7E6';
const SELECTED_BG = '#FFF3E0';
const SELECTED_HOVER_BG = '#FFE0B2';

const COLUMN_WIDTHS: Record<string, string> = {
	name: '13%',
	firstName: '11%',
	email: '22%',
	username: '13%',
	hasTarifAccess: '15%'
};

const stickyCell = {
	position: 'sticky',
	left: 0,
	zIndex: 1
} as const;

const headerCellSx = {
	color: 'common.black',
	fontFamily: DIN,
	fontStyle: 'normal',
	fontWeight: 500,
	letterSpacing: '-0.05px',
	fontSize: { xs: '10px', sm: '14px' },
	lineHeight: { xs: '16px', sm: '24px' },
	border: 'none',
	pl: 0,
	pr: { xs: 10, sm: 0 },
	py: 0,
	pb: { xs: 1, md: 2.5 },
	whiteSpace: 'nowrap'
} as const;

const bodyCellSx = {
	color: 'text.secondary',
	fontFamily: DIN,
	fontSize: '14px',
	fontWeight: 400,
	lineHeight: '24px',
	letterSpacing: '-0.05px',
	border: 'none',
	pl: 0,
	pr: { xs: 4, sm: 0 },
	py: { xs: 2, sm: 4 },
	whiteSpace: 'nowrap'
} as const;

type EmployeeTableProps = {
	employees: Employee[];
	columns: EmployeeTableColumn[];
	selectedIds: Set<string>;
	page: number;
	onToggleItem: (id: string) => void;
	onToggleAll: () => void;
	onEdit: (employee: Employee) => void;
	isAllSelected: boolean;
	isIndeterminate: boolean;
};

const EmployeeTable = ({
	employees,
	columns,
	selectedIds,
	page,
	onToggleItem,
	onToggleAll,
	onEdit,
	isAllSelected,
	isIndeterminate
}: EmployeeTableProps) => {
	const prefersReducedMotion = useReducedMotion();

	return (
		<TableContainer
			sx={{
				overflowX: 'auto',
				scrollbarWidth: 'none',
				'&::-webkit-scrollbar': { display: 'none' },
				px: 0,
				width: { xs: '100vw', md: '100%' },
				marginLeft: { xs: 'calc(50% - 50vw)', md: 0 },
				marginRight: { xs: 'calc(50% - 50vw)', md: 0 }
			}}
		>
			<Table
				size="small"
				sx={{
					tableLayout: 'fixed',
					width: '100%',
					minWidth: 720,
					'& tbody .MuiTableCell-root': {
						border: 'none',
						height: { xs: 32, sm: '72px' },
						minHeight: { xs: 32, sm: 'unset' },
						py: { xs: 2, sm: 4 }
					},
					'& tr > .MuiTableCell-root:first-of-type': {
						pl: 7,
						pr: { xs: 4, md: 12 },
						position: { xs: 'sticky', md: 'static' },
						left: { xs: 0, md: 'auto' },
						zIndex: { xs: 10, md: 'auto' },
						isolation: 'isolate'
					},
					'& tr > .MuiTableCell-root:nth-of-type(2)': {
						pl: { xs: 4, md: 0 }
					},
					'& tr > .MuiTableCell-root:last-of-type': {
						pr: { xs: 5, sm: 0 }
					},
					'& thead .MuiTableCell-root': {
						border: 'none',
						py: 0,
						pb: { xs: 1, md: 2.5 }
					},
					'& thead tr > .MuiTableCell-root:first-of-type': {
						'&::after': {
							content: '""',
							position: 'absolute',
							top: 0,
							bottom: 8,
							right: 0,
							width: '1px',
							backgroundColor: 'divider',
							zIndex: 1,
							display: { xs: 'block', md: 'none' },
							pointerEvents: 'none'
						}
					}
				}}
			>
				<TableHead>
					<TableRow>
						<TableCell
							padding="none"
							sx={{
								...headerCellSx,
								...stickyCell,
								bgcolor: 'common.white',
								zIndex: 2,
								width: '12%'
							}}
						>
							<Checkbox
								checked={isAllSelected}
								indeterminate={isIndeterminate}
								onChange={onToggleAll}
								size="small"
								slotProps={{
									input: {
										'aria-label': isAllSelected ? 'Alle Mitarbeiter abwählen' : 'Alle Mitarbeiter auswählen'
									}
								}}
								sx={{
									color: 'text.tertiary',
									borderRadius: '4px',
									transition: 'color 160ms ease, background-color 160ms ease',
									'&:hover': {
										bgcolor: 'action.hover',
										color: 'text.secondary'
									}
								}}
							/>
						</TableCell>
						{columns.map((col) => (
							<TableCell
								key={col.key}
								align={col.key === 'hasTarifAccess' ? 'center' : 'left'}
								sx={{
									...headerCellSx,
									width: COLUMN_WIDTHS[col.key]
								}}
							>
								<Typography
									variant="body-sm"
									component="span"
									sx={{
										fontWeight: 500,
										color: 'common.black'
									}}
								>
									{col.label}
								</Typography>
							</TableCell>
						))}
						<TableCell sx={{ ...headerCellSx, width: '14%' }} align="center">
							<Typography
								variant="body-sm"
								component="span"
								sx={{
									fontWeight: 500,
									color: 'common.black'
								}}
							>
								bearbeiten
							</Typography>
						</TableCell>
					</TableRow>
				</TableHead>
				<AnimatePresence mode="wait">
					<TableBody key={page}>
						{employees.map((employee, idx) => {
							const isSelected = selectedIds.has(employee.id);
							const rowBg = isSelected ? SELECTED_BG : idx % 2 === 0 ? STRIPE_BG : 'common.white';
							const rowHoverBg = isSelected ? SELECTED_HOVER_BG : HOVER_BG;

							return (
								<MotionTableRow
									key={employee.id}
									initial={prefersReducedMotion ? false : { opacity: 0, y: 10 }}
									animate={{ opacity: 1, y: 0 }}
									transition={
										prefersReducedMotion
											? { duration: 0 }
											: {
													duration: 0.35,
													ease: [0, 0, 0.2, 1],
													delay: idx * 0.05
												}
									}
									sx={{
										bgcolor: rowBg,
										'&:hover td': { bgcolor: rowHoverBg },
										transition: 'background-color 120ms ease'
									}}
								>
									<TableCell
										padding="none"
										sx={{
											...bodyCellSx,
											...stickyCell,
											bgcolor: rowBg,
											'tr:hover &': { bgcolor: rowHoverBg }
										}}
									>
										<Checkbox
											checked={isSelected}
											onChange={() => onToggleItem(employee.id)}
											size="small"
											slotProps={{
												input: {
													'aria-label': `${employee.firstName} ${employee.name} ${isSelected ? 'abwählen' : 'auswählen'}`
												}
											}}
											sx={{
												color: 'text.tertiary',
												borderRadius: '4px',
												transition: 'color 160ms ease, background-color 160ms ease',
												'&:hover': {
													bgcolor: 'action.hover',
													color: 'text.secondary'
												}
											}}
										/>
									</TableCell>
									{columns.map((col) => (
										<TableCell
											key={col.key}
											align={col.key === 'hasTarifAccess' ? 'center' : 'left'}
											sx={{
												...bodyCellSx,
												...(['name', 'firstName', 'email'].includes(col.key) && {
													overflow: 'hidden',
													textOverflow: 'ellipsis'
												})
											}}
										>
											{col.key === 'hasTarifAccess' ? (employee.hasTarifAccess ? 'Ja' : 'Nein') : employee[col.key]}
										</TableCell>
									))}
									<TableCell sx={bodyCellSx} align="center">
										<IconButton
											size="small"
											onClick={() => onEdit(employee)}
											aria-label={`${employee.name} bearbeiten`}
											sx={{
												color: 'text.tertiary',
												p: 1,
												borderRadius: '6px',
												transition: 'color 160ms ease, background-color 160ms ease',
												'&:hover': {
													bgcolor: 'action.hover',
													color: 'text.primary'
												}
											}}
										>
											<EditOutlinedIcon fontSize="small" />
										</IconButton>
									</TableCell>
								</MotionTableRow>
							);
						})}
					</TableBody>
				</AnimatePresence>
			</Table>
		</TableContainer>
	);
};

export default EmployeeTable;
